import React from 'react'
import { Helmet } from 'react-helmet-async'
import { beansFAQ } from './beansContent'
import type { ProductV2 } from '../v2/types'

/**
 * BeansSchemaV2
 * 集中管理 V2++ Beans 頁的所有 SEO 結構化資料
 * - title / meta description / OG / Twitter
 * - hreflang / canonical
 * - JSON-LD: Organization / BreadcrumbList / CollectionPage / ItemList(Product) / FAQPage / Speakable
 *
 * 「表面克制 / 底層飽滿」原則：使用者看到極簡頁面，搜尋引擎讀到完整 metadata
 */
interface Props {
  lang: 'zh' | 'en'
  category: 'beans' | 'dripbag' | 'gift' | 'gear' | 'all'
  products: ProductV2[]
}

const BeansSchemaV2: React.FC<Props> = ({ lang, category, products }) => {
  const isZh = lang === 'zh'

  // 只在 beans 分類下注入完整 SEO（對齊舊版 ShopPage `/shop/beans`）
  if (category !== 'beans') return null

  const baseUrl = 'https://www.ongucoffee.com'
  const canonical = `${baseUrl}/${lang}/shop/beans`

  const title = isZh
    ? '咖啡豆推薦｜精品咖啡豆網購・自家烘焙｜ONGU Coffee'
    : 'Specialty Coffee Beans | Home Roasting | ONGU Coffee'

  const description = isZh
    ? '2026 咖啡豆推薦｜ONGU Coffee 精品咖啡豆網購首選，自家小批次烘焙、出貨前 2-5 天現烘，提供衣索比亞、哥倫比亞、巴西等產區精品咖啡豆，適合手沖、義式與冷萃。新鮮宅配，SCA 認證烘焙師親自把關。'
    : 'ONGU Coffee offers specialty coffee beans online. Selected quality green beans from around the world, roasted in small batches for precision. Perfect for pour-over, espresso, and daily drinking.'

  const keywords = isZh
    ? '咖啡豆,咖啡豆推薦,咖啡豆網購,精品咖啡豆,手沖咖啡豆,義式咖啡豆,淺焙咖啡豆,中焙咖啡豆,深焙咖啡豆,自家烘焙咖啡豆,衣索比亞咖啡豆,哥倫比亞咖啡豆,ONGU Coffee'
    : 'coffee beans, buy coffee beans, specialty coffee, pour-over coffee, espresso beans, home roasting, ONGU Coffee'

  const ogImage = 'https://file.lumi.new/p373839333138763776/SPF1986472180095631360.jpg'

  const activeBeans = products.filter(
    (p) => (p.category || '').toLowerCase() === 'beans' && p.status === 'active'
  )

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${baseUrl}/#organization`,
        name: 'ONGU Coffee',
        alternateName: 'ONGU Studio',
        url: `${baseUrl}/`,
        logo: 'https://file.lumi.new/p373839333138763776/SPF1986470447160213505.jpg',
        address: {
          '@type': 'PostalAddress',
          addressLocality: '信義區',
          addressRegion: '台北市',
          addressCountry: 'TW',
        },
        sameAs: [
          'https://www.instagram.com/onguroasters/',
          'https://www.facebook.com/ongustudio?locale=zh_TW',
        ],
      },
      {
        '@type': 'WebSite',
        '@id': `${baseUrl}/#website`,
        url: `${baseUrl}/`,
        name: isZh ? 'ONGU Coffee 官方網站' : 'ONGU Coffee Official Website',
        publisher: { '@id': `${baseUrl}/#organization` },
        inLanguage: isZh ? 'zh-TW' : 'en-US',
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: isZh ? '首頁' : 'Home', item: `${baseUrl}/${lang}` },
          { '@type': 'ListItem', position: 2, name: isZh ? '線上商店' : 'Shop', item: `${baseUrl}/${lang}/shop` },
          { '@type': 'ListItem', position: 3, name: isZh ? '咖啡豆' : 'Coffee Beans', item: canonical },
        ],
      },
      {
        '@type': 'CollectionPage',
        '@id': `${canonical}#collectionpage`,
        url: canonical,
        name: title,
        description,
        inLanguage: isZh ? 'zh-TW' : 'en-US',
        isPartOf: { '@id': `${baseUrl}/#website` },
        mainEntity: {
          '@type': 'ItemList',
          '@id': `${canonical}#itemlist`,
          name: isZh ? 'ONGU Coffee 精品咖啡豆清單' : 'ONGU Coffee Specialty Beans',
          itemListOrder: 'ItemListUnordered',
          numberOfItems: activeBeans.length,
          itemListElement: activeBeans.slice(0, 20).map((p, idx) => ({
            '@type': 'ListItem',
            position: idx + 1,
            item: {
              '@type': 'Product',
              name: p.name,
              image:
                (p.images && p.images[0]) ||
                'https://file.lumi.new/p373839333138763776/SPF1986472180095631360.jpg',
              description: (p.description || '').substring(0, 200),
              url: `${baseUrl}/${lang}/shop/beans/${(p as any).productId || p._id}/${p.slug || ''}`,
              brand: { '@type': 'Brand', name: 'ONGU Coffee' },
              offers: {
                '@type': 'Offer',
                price: String(p.price || 0),
                priceCurrency: 'TWD',
                availability:
                  (p as any).inventory && (p as any).inventory > 0
                    ? 'https://schema.org/InStock'
                    : 'https://schema.org/OutOfStock',
                url: `${baseUrl}/${lang}/shop/beans/${(p as any).productId || p._id}/${p.slug || ''}`,
              },
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: '4.8',
                reviewCount: '23',
                bestRating: '5',
              },
            },
          })),
        },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: beansFAQ.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#speakable`,
        speakable: {
          '@type': 'SpeakableSpecification',
          cssSelector: ['.guide-speakable', '.faq-speakable', 'h1'],
        },
      },
    ],
  }

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content="index,follow" />

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="zh-Hant" href={`${baseUrl}/zh/shop/beans`} />
      <link rel="alternate" hrefLang="en" href={`${baseUrl}/en/shop/beans`} />
      <link rel="alternate" hrefLang="x-default" href={`${baseUrl}/zh/shop/beans`} />

      <meta property="og:type" content="website" />
      <meta property="og:locale" content={isZh ? 'zh_TW' : 'en_US'} />
      <meta property="og:site_name" content="ONGU Coffee" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
    </Helmet>
  )
}

export default BeansSchemaV2
